import type { ShootingStarAttack } from '../../../data/full/celeste';
import { angleOf } from '../../../shared/angle';
import { clamp, clamp01 } from '../../../shared/scalar';
import { isBeatTick } from '../../../shared/tempo';
import { fan } from '../../attacks/fan';
import type { AttackModule } from '../../attacks/types';
import { pullTrigger } from '../../attacks/trigger';
import { hurtEnemy, keepWhere, touches } from '../../effects';
import type { PlayerState, PropState, SimState, Vec2, ZoneState } from '../../state';
import { statValue } from '../../stats';
import type { StepContext } from '../../systems/types';
import { layZone } from '../../zones';
import { flightOver, flyThrough, ownerOf } from '../../props';
import { NOTE } from './echo';

export const SHOOTING_STAR = 'shootingStar';

export interface Line {
  readonly from: Vec2;
  readonly to: Vec2;
  readonly owner: PlayerState;
  readonly attack: ShootingStarAttack;
}

export const shootingStar: AttackModule<ShootingStarAttack> = {
  reads: [
    'attackCooldownMul',
    'attackRadiusMul',
    'damageMul',
    'pierceAdd',
    'projectileCountAdd',
    'projectileSpeedMul',
  ],

  step({ state }, player, attack, input) {
    if (!pullTrigger(player, input.fire, attack.cooldownTicks)) {
      return;
    }
    const count = Math.max(1, Math.floor(statValue(player, 'projectileCountAdd', 1)));
    const speed = statValue(player, 'projectileSpeedMul', attack.speed);
    const damage = statValue(player, 'damageMul', attack.damage);
    const hits = statValue(player, 'pierceAdd', 0) + 1;
    for (const way of fan(player.aim, count, attack.spreadRadians)) {
      const star: PropState = {
        id: state.nextEntityId,
        kind: SHOOTING_STAR,
        playerId: player.id,
        phase: 'flying',
        x: player.x,
        y: player.y,
        prevX: player.x,
        prevY: player.y,
        vx: way.x * speed,
        vy: way.y * speed,
        damage,
        ticksLeft: attack.rangeTicks,
        hitsLeft: hits,
      };
      state.nextEntityId += 1;
      (state.props ??= []).push(star);
    }
    state.events.push({
      type: 'playerFired',
      playerId: player.id,
      x: player.x,
      y: player.y,
      angle: angleOf(player.aim),
    });
  },

  update(ctx) {
    const { state, tempo } = ctx;
    const lines = constellations(ctx);
    if (lines.length > 0) {
      boostShots(state, lines);
      if (isBeatTick(state.tick, tempo)) {
        strike(ctx, lines);
      }
    }
    const props = state.props;
    if (!props?.some((prop) => prop.kind === SHOOTING_STAR)) {
      return;
    }
    ctx.enemyGrid.rebuild(state.enemies);
    let fallen = false;
    for (const prop of props) {
      if (prop.kind === SHOOTING_STAR) {
        fallen = fly(ctx, prop) || fallen;
      }
    }
    if (fallen) {
      keepWhere(props, (prop) => prop.kind !== SHOOTING_STAR || prop.hitsLeft > 0);
      if (props.length === 0) {
        delete state.props;
      }
    }
  },

  reach(attack, player) {
    return statValue(player, 'projectileSpeedMul', attack.speed) * attack.rangeTicks;
  },
};

function shootingStarOf(
  ctx: Pick<StepContext, 'content'>,
  owner: PlayerState,
): ShootingStarAttack | undefined {
  const attack = ctx.content.classes.get(owner.classId)?.attack;
  return attack?.kind === 'shootingStar' ? attack : undefined;
}

export function starLinks(stars: readonly Vec2[], reach: number): [number, number][] {
  const links: [number, number][] = [];
  for (let i = 0; i < stars.length; i++) {
    for (let j = i + 1; j < stars.length; j++) {
      const a = stars[i];
      const b = stars[j];
      if (a === undefined || b === undefined) {
        continue;
      }
      const dx = b.x - a.x;
      const dy = b.y - a.y;
      if (dx * dx + dy * dy <= reach * reach) {
        links.push([i, j]);
      }
    }
  }
  return links;
}

export function linkReach(owner: PlayerState, attack: ShootingStarAttack): number {
  return statValue(owner, 'attackRadiusMul', attack.constellation.reach);
}

export function starsOf(
  zones: readonly ZoneState[] | undefined,
  owner: PlayerState,
  attack: ShootingStarAttack,
): ZoneState[] {
  return (zones ?? []).filter(
    (zone) => zone.playerId === owner.id && zone.definition.id === attack.star.id,
  );
}

function constellations(ctx: Pick<StepContext, 'state' | 'content'>): Line[] {
  const { state } = ctx;
  const lines: Line[] = [];
  if (state.zones === undefined) {
    return lines;
  }
  for (const owner of state.players) {
    const attack = shootingStarOf(ctx, owner);
    if (attack === undefined) {
      continue;
    }
    const stars = starsOf(state.zones, owner, attack);
    for (const [i, j] of starLinks(stars, linkReach(owner, attack))) {
      const from = stars[i];
      const to = stars[j];
      if (from !== undefined && to !== undefined) {
        lines.push({ from, to, owner, attack });
      }
    }
  }
  return lines;
}

// Half-open, so a straight flight cut in moves crosses a line once, never on two moves.
export function crossing(from: Vec2, step: Vec2, a: Vec2, b: Vec2): number | undefined {
  const qx = b.x - a.x;
  const qy = b.y - a.y;
  const denominator = step.x * qy - step.y * qx;
  if (denominator === 0) {
    return undefined;
  }
  const px = a.x - from.x;
  const py = a.y - from.y;
  const along = (px * qy - py * qx) / denominator;
  const across = (px * step.y - py * step.x) / denominator;
  return along >= 0 && along < 1 && across >= 0 && across <= 1 ? along : undefined;
}

// A straight shot or a flying note crosses each line once; a lob flies over, a returning frisbee
// hurts no more.
function boostShots(state: SimState, lines: readonly Line[]): void {
  for (const shot of state.projectiles) {
    if (
      shot.owner.kind !== 'enemy' &&
      shot.arc === undefined &&
      shot.returning !== true &&
      shot.damage > 0
    ) {
      shot.damage *= boostAcross(shot, { x: shot.vx, y: shot.vy }, lines);
    }
  }
  for (const prop of state.props ?? []) {
    if (prop.kind === SHOOTING_STAR || (prop.kind === NOTE && prop.phase === 'flying')) {
      prop.damage *= boostAcross(prop, { x: prop.vx, y: prop.vy }, lines);
    }
  }
}

function boostAcross(from: Vec2, step: Vec2, lines: readonly Line[]): number {
  let boost = 1;
  for (const line of lines) {
    if (crossing(from, step, line.from, line.to) !== undefined) {
      boost *= 1 + line.attack.constellation.boost;
    }
  }
  return boost;
}

function strike(ctx: StepContext, lines: readonly Line[]): void {
  const { state } = ctx;
  for (const enemy of state.enemies) {
    if (enemy.hp <= 0) {
      continue;
    }
    for (const line of lines) {
      const at = nearestOn(line, enemy);
      if (touches(enemy, at, line.attack.constellation.width / 2)) {
        const damage = statValue(line.owner, 'damageMul', line.attack.constellation.damage);
        hurtEnemy(state, enemy, damage, line.owner.id, at);
      }
    }
  }
}

function nearestOn({ from, to }: Line, body: Vec2): Vec2 {
  const dx = to.x - from.x;
  const dy = to.y - from.y;
  const length = dx * dx + dy * dy;
  const share =
    length === 0 ? 0 : clamp01(((body.x - from.x) * dx + (body.y - from.y) * dy) / length);
  return { x: from.x + dx * share, y: from.y + dy * share };
}

function fly(ctx: StepContext, prop: PropState): boolean {
  const owner = ownerOf(ctx.state, prop);
  const attack = shootingStarOf(ctx, owner);
  if (attack === undefined) {
    throw new Error(`player ${String(owner.id)} of class "${owner.classId}" shoots no star`);
  }
  if (!flyThrough(ctx, prop, owner, attack.radius) && !flightOver(ctx, prop, attack.radius)) {
    return false;
  }
  const { width, height } = ctx.state.arena;
  prop.x = clamp(prop.x, 0, width);
  prop.y = clamp(prop.y, 0, height);
  layZone(ctx, owner, attack.star, { x: prop.x, y: prop.y, direction: { x: prop.vx, y: prop.vy } });
  prop.hitsLeft = 0;
  return true;
}
